import { memo } from "react";
import { Link } from "react-router-dom";
import { Mail, Shield, Truck, CreditCard, Sparkles, Star, Facebook, Instagram, Package } from "lucide-react";
import { KlaviyoForm } from "./KlaviyoForm";
import vapebolagLogo from "@/assets/vapebolag-logo.webp";

// Payment methods as simple styled badges
const paymentMethods = [
  { name: "Visa", bg: "bg-[#1A1F71]", text: "text-white" },
  { name: "Google Pay", bg: "bg-white", text: "text-gray-800" },
  { name: "Revolut", bg: "bg-[#0666EB]", text: "text-white" },
];

const socialLinks = [
  { href: "https://www.facebook.com/Vapebolag.se/", icon: Facebook, label: "Facebook" },
  { href: "https://www.instagram.com/vapebolag/", icon: Instagram, label: "Instagram" },
  { href: "https://www.tiktok.com/@vapebolag.se", icon: "tiktok", label: "TikTok" },
];

const productLinks = [
  { href: "/kollektioner/alla-produkter", label: "Alla produkter" },
  { href: "/kollektioner/thca-vapes", label: "THCA Vapes" },
  { href: "/kollektioner/thca-buds", label: "THCA Buds" },
  { href: "/kollektioner/thca-hash", label: "THCA Hash" },
  { href: "/kollektioner/cartridges", label: "Cartridges" },
  { href: "/kollektioner/bundles", label: "Bundles" },
];

const infoLinks = [
  { href: "/blogg", label: "Blogg" },
  { href: "/faq", label: "FAQ" },
  { href: "/om-oss", label: "Om oss" },
  { href: "/blogg/vad-ar-thca", label: "Vad är THCA?" },
  { href: "/blogg/thca-vape-sverige", label: "THCA Vape Guide" },
];

const customerLinks = [
  { href: "/kontakt", label: "Kontakta oss" },
  { href: "/frakt-leverans", label: "Frakt & Leverans" },
  { href: "/returer", label: "Returer & Byten" },
  { href: "/spara-order", label: "Spåra order" },
  { href: "/betalning", label: "Betalningsalternativ" },
];

const legalLinks = [
  { href: "/integritetspolicy", label: "Integritetspolicy" },
  { href: "/kopvillkor", label: "Köpvillkor" },
  { href: "/cookies", label: "Cookies" },
  { href: "/leveransvillkor", label: "Leveransvillkor" },
];

const trustBadges = [
  { icon: Shield, text: "100% Lagligt" },
  { icon: Truck, text: "Snabb Leverans" },
  { icon: CreditCard, text: "Säker Betalning" },
];

export const Footer = memo(() => {
  return (
    <footer className="relative overflow-hidden bg-card/50 border-t border-border pb-20 lg:pb-0">
      {/* Removed heavy blur for better performance */}
      
      {/* Newsletter section */}
      <div className="border-b border-border py-16 relative">
        <div className="container mx-auto px-4">
          <div className="bg-card/80 backdrop-blur-sm rounded-3xl border border-border shadow-2xl shadow-black/20 p-8 md:p-12">
            <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
              <div className="text-center lg:text-left">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full cta-gradient font-bold text-sm mb-4 shadow-lg">
                  <Sparkles className="w-4 h-4" />
                  EXKLUSIVT ERBJUDANDE
                </div>
                <h3 className="text-3xl md:text-4xl font-bold text-foreground mb-2">
                  Få <span className="text-gradient">10% rabatt</span>
                </h3>
                <p className="text-lg text-muted-foreground">på din första order när du prenumererar</p>
              </div>
              <div className="w-full lg:w-auto lg:min-w-[450px]">
                <KlaviyoForm listId="VNu46y" source="footer" compact />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Trust badges */}
      <div className="border-b border-border py-10">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-8 md:gap-16">
            {trustBadges.map((badge, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-primary/10 border border-primary/30 flex items-center justify-center shadow-lg shadow-primary/10">
                  <badge.icon className="w-6 h-6 text-primary" />
                </div>
                <span className="font-bold text-foreground">{badge.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12 md:py-16 relative">
        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8 md:gap-10 mb-12">
          {/* Brand */}
          <div className="col-span-2 sm:col-span-2 md:col-span-3 lg:col-span-2">
            <Link to="/" className="flex items-center gap-3 mb-6 group">
              <img 
                src={vapebolagLogo} 
                alt="Vapebolag – THCA vapes och cannabis produkter Sverige" 
                className="w-14 h-14 rounded-xl object-contain group-hover:scale-105 transition-transform"
              />
              <div>
                <span className="text-2xl font-bold text-foreground group-hover:text-primary transition-colors">
                  Vapebolag
                </span>
                <span className="text-sm text-muted-foreground block">THCA Vape Sverige</span>
              </div>
            </Link>
            <p className="text-muted-foreground mb-6 max-w-sm leading-relaxed">
              Sveriges ledande butik för premium THCA Vapes, Buds och Hash. 
              100% lagligt, labbtestat och snabb leverans till hela Sverige.
            </p>
            
            
            <a 
              href="mailto:info@snusparty.se" 
              className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-card border border-border hover:border-primary/50 transition-all text-muted-foreground hover:text-primary"
            >
              <Mail className="w-5 h-5" />
              info@snusparty.se
            </a>
            
            {/* PostNord Shipping Badge */}
            <div className="flex items-center gap-3 mt-4 px-4 py-3 rounded-xl bg-card border border-border">
              <Package className="w-5 h-5 text-primary" />
              <div className="flex flex-col">
                <span className="text-sm font-bold text-foreground">PostNord</span>
                <span className="text-xs text-muted-foreground">Snabb & pålitlig frakt</span>
              </div>
            </div>
            
            {/* Trustpilot Link - without rating display */}
            <a 
              href="https://se.trustpilot.com/review/snusparty.se"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 mt-3 px-4 py-3 rounded-xl bg-card border border-border hover:border-primary/50 transition-all group"
            >
              <svg className="w-5 h-5 text-[#00b67a]" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/>
              </svg>
              <div className="flex flex-col">
                <span className="text-sm font-bold text-foreground group-hover:text-primary transition-colors">Trustpilot</span>
                <span className="text-xs text-muted-foreground">Läs våra recensioner</span>
              </div>
            </a>
            
            {/* Social Links */}
            <div className="flex items-center gap-3 mt-4">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.label}
                  className="w-10 h-10 rounded-xl bg-card border border-border hover:border-primary/50 flex items-center justify-center text-muted-foreground hover:text-primary transition-all"
                >
                  {social.icon === "tiktok" ? (
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                    </svg>
                  ) : (
                    <social.icon className="w-5 h-5" />
                  )}
                </a>
              ))}
            </div>
          </div>

          {/* Products */}
          <div>
            <h4 className="font-bold mb-5 text-foreground text-lg">Produkter</h4>
            <ul className="space-y-3">
              {productLinks.map((link) => (
                <li key={link.href}>
                  <Link to={link.href} className="text-muted-foreground hover:text-primary transition-colors">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Information */}
          <div>
            <h4 className="font-bold mb-5 text-foreground text-lg">Information</h4>
            <ul className="space-y-3">
              {infoLinks.map((link) => (
                <li key={link.href}>
                  <Link to={link.href} className="text-muted-foreground hover:text-primary transition-colors">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Customer Service */}
          <div>
            <h4 className="font-bold mb-5 text-foreground text-lg">Kundtjänst</h4>
            <ul className="space-y-3">
              {customerLinks.map((link) => (
                <li key={link.href}>
                  <Link to={link.href} className="text-muted-foreground hover:text-primary transition-colors">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-border pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <p className="text-sm text-muted-foreground text-center md:text-left">
              © {new Date().getFullYear()} Vapebolag Sverige. Alla rättigheter förbehållna.
            </p>
            <div className="flex flex-wrap justify-center gap-6 text-sm text-muted-foreground">
              {legalLinks.map((link) => (
                <Link key={link.href} to={link.href} className="hover:text-primary transition-colors">
                  {link.label}
                </Link>
              ))}
            </div>
            <div className="flex items-center gap-3 text-sm">
              <span className="text-muted-foreground">Säker betalning:</span>
              <div className="flex items-center gap-2">
                {paymentMethods.map((method) => (
                  <span 
                    key={method.name} 
                    className={`px-3 py-1.5 rounded-lg ${method.bg} ${method.text} text-xs font-bold`}
                  >
                    {method.name}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
});

Footer.displayName = "Footer";
